import { jsonConstants } from '@thoughtspot/json-constants';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import {
    getAccessToken,
    getCurrentOrgId,
    getOktaUserId,
    getUserPrivileges,
    isImpersonatedSession,
} from '@thoughtspot/session-service';
import { getClientId, getIdpDomain } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import React, { Suspense, useEffect } from 'react';
import { SessionService } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { invalidateSessionInfoCallCache } from '/@utils/service-worker.util';
import {
    LearningModuleContainer,
    TopNavV3,
} from '../../microfrontends/global-nav';
import { hasDataTabAccessPrivileges } from '../../routing/privilege-list';
import {
    getAppModule,
    getIsOverlayLeftPanel,
    useIsInsightsPage,
} from '../../routing/routes-util';
import { NotificationContainer } from '../coms-notification/notification.container';
import { ImpersonationBorder } from '../impersonation/impersonation-border';
import { ImpersonationIndicatorContainer } from '../impersonation/impersonation-indicator.container';
import { UserMenuContainerWithInvite } from '../user-menu/user-menu.container';
import { useAnalystStudioMutation } from './custom-action-hooks/useAnalystStudioCustomHook';
import { NavSearchBarContainer } from './nav-search-bar.container';
import { LeftNavigationOverlayHub } from './navigation/left-navigation-overlay-hub.container';

export type NavLinkURI = {
    uri: string;
    linkTarget?: '_blank' | '_self';
    routerURI: never;
    callback: never;
    isReactShellSupported: boolean;
};

export type NavLinkCallback = {
    callback: () => void;
    routerURI?: string;
    uri: never;
    linkTarget: never;
    isReactShellSupported: never;
};

export declare type NavLinkWithTitleIconAndCallback = NavLinkCallback & {
    titleIconId: IconID;
};

export type NavLinkConfig = {
    name: string;
    id?: string;
    icon?: IconID;
    description?: string;
    target: NavLinkURI | NavLinkCallback | NavLinkWithTitleIconAndCallback;
};

export interface QuickLinkItem {
    id: string;
    label: string;
    action: () => void;
    path: string;
    hide?: boolean;
}

interface GlobalNavV3Props {
    sessionService?: SessionService;
    homeButtonConfig: NavLinkConfig;
    renderSearchBar?: boolean;
    applicationSwitcherConfig?: NavLinkConfig[];
    searchBar?: React.ReactChild;
    userProfile?: React.ReactChild;
    activeApplicationID?: string;
    triggerV1Logout?: () => void;
    shouldShowPendoHelp: boolean;
    useAddUserToModeMutation: any;
    useGetAnalystStudioAccessInfoLazyQuery: any;
    isCopilotEnabled?: boolean;
    customQuickLinks?: QuickLinkItem[];
    isEmbedded?: boolean;
    isEurekaPath?: boolean;
    themeType: string;
}

export const GlobalNavV3: React.FC<GlobalNavV3Props> = props => {
    const [
        showLeftNavigationOverlayHub,
        setShowLeftNavigationOverlayHub,
    ] = React.useState(false);
    /**
     * Determines whether to display the hamburger icon in the top navigation.
     */
    const showHamburgerIcon = () => {
        const isInsightsPage = useIsInsightsPage();
        const canShowInsightsTab = !props.sessionService?.hideHomepageLeftNav(
            true,
        );
        const canAccessDataTab = hasDataTabAccessPrivileges();
        const hasDeveloperAccess = props.sessionService?.isEverywhereEnabled();
        const hideTabSwitcher = props.sessionService?.isApplicationSwitcherHidden();
        const isOverlayLeftPanel = getIsOverlayLeftPanel();

        // No tabs are available then don't show the hamburger icon
        if (!canShowInsightsTab && !canAccessDataTab && !hasDeveloperAccess) {
            return false;
        }

        // When the TabSwitcher is hidden,
        // hamburger icon will be shown only if the overlayLeftPanel is visible
        // to show the current page left nav items.
        if (hideTabSwitcher) {
            // Both tabSwitcher and overlayLeftPanel are hidden then don't show the hamburger icon
            if (!canShowInsightsTab) {
                return false;
            }
            // If the overlayLeftPanel is visible, then show the hamburger icon
            if (isOverlayLeftPanel) {
                return true;
            }
            // otherwise don't show the hamburger icon
            return false;
        }

        // When the TabSwitcher is visible and insights left nav is hidden
        // then show the hamburger icon to switch to data or develop
        if (isInsightsPage && !canShowInsightsTab) {
            if (canAccessDataTab || hasDeveloperAccess) {
                return true;
            }
            return false;
        }

        // If the overlayLeftPanel is visible, then show the hamburger icon
        if (isOverlayLeftPanel) {
            return true;
        }

        return false;
    };

    const isOverlayLeftPanel = showHamburgerIcon();

    const hamburgerIconHoverHandlerDebounce = React.useCallback(
        _.debounce((value: boolean) => {
            // Just to retain the open state of the overlay,
            // It should not open the overlay.
            if (value) {
                return;
            }

            // if popover is open, do not close the overlay
            const el = document.querySelector('.popover-module__in');
            if (el) {
                return;
            }

            setShowLeftNavigationOverlayHub(value);
        }, 100),
        [],
    );

    const hamburgerIconHoverHandler = (
        value: boolean,
        fromHamburgerIcon = false,
        forceToClose = false,
    ) => {
        if (fromHamburgerIcon) {
            // Close the popovers before opening the overlay.
            // Blur first so popovers tied to input focus (eureka suggestions)
            // dismiss; dispatchEvent handles popovers that listen on document
            // mousedown for outside-click.
            (document.activeElement as HTMLElement | null)?.blur?.();
            document.dispatchEvent(new MouseEvent('mousedown'));

            setShowLeftNavigationOverlayHub(true);
            return;
        }

        // forceToClose on mousedown outside the overlay
        if (forceToClose) {
            setShowLeftNavigationOverlayHub(false);
            return;
        }

        hamburgerIconHoverHandlerDebounce(value);
    };

    const {
        useAddUserToModeMutation,
        useGetAnalystStudioAccessInfoLazyQuery,
    } = props;

    const appSwitcherProps = { ...props };

    // Initialize mutation functions for Analyst Studio
    const { onAnalystStudioClicked } = useAnalystStudioMutation(
        getAccessToken, // Access token for authentication
        getOktaUserId, // User ID from Okta
        getUserPrivileges, // User privileges for access control
        getCurrentOrgId, // Current organization ID
        useAddUserToModeMutation, // Mutation hook for adding user to a mode
        useGetAnalystStudioAccessInfoLazyQuery, // Lazy query hook for fetching Analyst Studio access info
        getIdpDomain, // IDP domain for configuration
        getClientId, // Client ID for configuration
    );

    // Find the index of the configuration object related to Analyst Studio
    const index = props.applicationSwitcherConfig.findIndex(
        config => config.id === jsonConstants.ANALYST_STUDIO_APP_ID,
    );

    // Retrieve the Analyst Studio configuration if it exists
    const analystStudioConfig =
        index !== -1 ? props.applicationSwitcherConfig[index] : null;

    // If the Analyst Studio configuration is found, update the callback if necessary
    if (analystStudioConfig) {
        // Set a new callback function for Analyst Studio
        analystStudioConfig.target.callback = () => {
            onAnalystStudioClicked(null, '', '');
        };
        (analystStudioConfig.target as NavLinkWithTitleIconAndCallback).titleIconId =
            IconID.OPEN_IN_NEW_TAB;
        // Update the configuration in the array
        appSwitcherProps.applicationSwitcherConfig[index] = analystStudioConfig;
    }

    const { isEmbedded = isEmbeddedApp() } = props;

    useEffect(() => {
        setShowLeftNavigationOverlayHub(false);
    }, [isOverlayLeftPanel]);

    useEffect(() => {
        if (showLeftNavigationOverlayHub) {
            window.dispatchEvent(new CustomEvent('insights-panel:preload'));
        }
    }, [showLeftNavigationOverlayHub]);

    const isNotificationVisible =
        props.sessionService?.isInAppNotificationEnabled() &&
        !props.sessionService?.isCopilotEnabled();

    return (
        <>
            {isImpersonatedSession() && <ImpersonationBorder />}
            <TopNavV3
                searchBarV3={
                    !props.isCopilotEnabled &&
                    props.renderSearchBar && (
                        <Suspense
                            fallback={
                                <LoadingIndicator.Global>
                                    <></>
                                </LoadingIndicator.Global>
                            }
                        >
                            <NavSearchBarContainer
                                /** This ensures the search bar to force re-render
                                 for new navigation v3 where it is shown in top nav even for search result pages  */
                                key={`nav-search-${props.isEurekaPath}`}
                                isEurekaPath={props.isEurekaPath}
                            />
                        </Suspense>
                    )
                }
                inAppNotification={
                    isNotificationVisible && <NotificationContainer />
                }
                impersonationIndicator={<ImpersonationIndicatorContainer />}
                shouldShowPendoHelp={props.shouldShowPendoHelp}
                learningModule={<LearningModuleContainer showHeader={false} />}
                userProfile={
                    <UserMenuContainerWithInvite
                        onEmailAdd={null}
                        triggerV1Logout={props.triggerV1Logout}
                    />
                }
                isEmbedded={isEmbedded}
                homeButtonConfig={props.homeButtonConfig}
                isOverlayLeftPanel={isOverlayLeftPanel}
                hamburgerIconHoverHandler={hamburgerIconHoverHandler}
                invalidateSessionInfoCallCache={invalidateSessionInfoCallCache}
                currentAppModule={getAppModule()}
                {...props}
            />
            {/* Show the left navigation overlay hub on mouse hover the hamburger icon */}
            {isOverlayLeftPanel && (
                <LeftNavigationOverlayHub
                    hamburgerIconHoverHandler={hamburgerIconHoverHandler}
                    showLeftNavigationOverlayHub={showLeftNavigationOverlayHub}
                    themeType={props.themeType}
                />
            )}
        </>
    );
};
export default GlobalNavV3;
